import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supportService } from '@/services/api/support.service';
import type { IssueListQuery, RaiseIssueRequest, UpdateIssueRequest } from '@/types';
import { queryKeys } from './keys';

/**
 * Server state for support issues.
 *
 * React Query owns fetching, caching and retry. Nothing about issues belongs in
 * Redux: it is server state, and a second copy in a store is a second thing to
 * keep in step with the server.
 */

export function useIssues(query: IssueListQuery = {}) {
  return useQuery({
    queryKey: queryKeys.issues.list(query),
    queryFn: ({ signal }) => supportService.list(query, signal),
    /*
     * Short, because a technician who has just raised an issue looks straight
     * back at this list, and one who is waiting on support refreshes it. Long
     * enough that switching filters twice does not refetch the same page.
     */
    staleTime: 30_000,
    // Paging the queue keeps the current page on screen until the next one
    // arrives, rather than flashing back to the loading skeleton.
    placeholderData: keepPreviousData,
  });
}

export function useRaiseIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: RaiseIssueRequest) => supportService.raise(body),
    onSuccess: async () => {
      // The broad key, so every filter combination currently cached is
      // refetched. A new issue belongs in some of them and not others, and
      // working out which here would be a second place to get it wrong.
      await queryClient.invalidateQueries({ queryKey: queryKeys.issues.all });
      toast.success('Issue raised', {
        description: 'Support can see it now. You will get an update on this page.',
      });
    },
  });
}

export function useUpdateIssue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ issueId, ...body }: UpdateIssueRequest & { issueId: string }) =>
      supportService.update(issueId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.issues.all });
    },
  });
}

/**
 * Who the caller may raise an issue for. More than one entry only for a lab
 * owner with teammates - which is exactly when the "On behalf of" picker shows.
 */
export function useIssueReporters() {
  return useQuery({
    queryKey: queryKeys.issues.reporters(),
    queryFn: ({ signal }) => supportService.reporters(signal),
    staleTime: 5 * 60 * 1000,
  });
}
